// "Add to calendar" / "Subscribe to calendar" helpers. The feed and the per-item .ics files are
// served by the teamCalendar Cloud Function (tools/cloud_functions/sitemaps). The item rules
// below (summary, location, 3h games, 1h default events, tbat all-day date) mirror
// lib/calendar.js there, so a Google/Outlook add matches the downloaded file.
// Spec: docs/superpowers/specs/2026-10-20-team-calendar-feed-design.md.
import { optimXStoreWebsite, optimXTestWebsite, optimXTesting } from './TeamInfo';
import { contrastRatio, getReadableTextColor } from './ColorContrast';

const HOUR = 60 * 60 * 1000;
const GAME_HOURS = 3;
const EVENT_DEFAULT_HOURS = 1;
const PLATFORM_HOSTS = ['test.optimxsports.com', 'www.test.optimxsports.com', 'store.optimxsports.com', 'www.store.optimxsports.com'];

export interface CalendarItem {
    title: string;
    start: Date;
    end: Date;
    allDay: boolean;
    location: string;
    url: string;
}

export interface CalendarFeedUrls {
    https: string;
    webcal: string;
    google: string;
    outlook: string;
}

const nonEmpty = (s: any): boolean => { return typeof s === 'string' && s.trim() !== ''; };

const toDate = (t: any): Date | null => {
    if (!t) return null;
    if (t instanceof Date) return t;
    if (typeof t.toDate === 'function') return t.toDate();
    return null;
};

// The team's canonical origin (accountObject.url), or '' when it isn't a clean public origin:
// never-published teams carry `www.test.optimxsports.com/?team=<id>`, and their calendar URLs
// would 404 (the function's isUnpublished).
export const getCalendarOrigin = (globalState: any): string => {
    const url = globalState && globalState.accountObject ? globalState.accountObject.url : null;
    if (!nonEmpty(url)) return '';
    const m = /^https?:\/\/([^/?#]+)\/?$/i.exec(url.trim());
    if (!m) return '';
    const host = m[1].toLowerCase();
    if (PLATFORM_HOSTS.includes(host)) return '';
    return `https://${host}`;
};

export const isCalendarAvailable = (globalState: any, flags = { store: optimXStoreWebsite, test: optimXTestWebsite, testing: optimXTesting }): boolean => {
    if (flags.store || flags.test || flags.testing) return false;
    return getCalendarOrigin(globalState) !== '';
};

export const getCalendarFeedUrls = (origin: string, teamName: string): CalendarFeedUrls => {
    const https = `${origin}/calendar.ics`;
    const webcal = https.replace(/^https:/, 'webcal:');
    return {
        https,
        webcal,
        google: `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(webcal)}`,
        outlook: `https://outlook.live.com/calendar/0/addfromweb?url=${encodeURIComponent(https)}&name=${encodeURIComponent(teamName || 'Team schedule')}`,
    };
};

// 'tbat' (time to be announced) games are stored at 23:59:59 admin-local on the right date;
// shifting back 10h before taking the UTC date recovers it for UTC-10 ... UTC+13:59.
export const getTbatDate = (t: Date): Date => {
    const d = new Date(t.getTime() - 10 * HOUR);
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
};

// game: the client's enriched game ({id, status, timeOfGame, homeGame, venue, opponentName, title}).
// Returns null for games that can't be added (tba, parked 2099 date, cancelled). Away games
// with no venue get no location here (the opponent's address isn't loaded client-side).
export const getGameCalendarItem = (game: any, teamName: string, teamInfo: any, origin: string): CalendarItem | null => {
    if (!game) return null;
    const start = toDate(game.timeOfGame);
    if (!start || game.status === 'tba' || game.status === 'cancelled' || start.getUTCFullYear() >= 2099) return null;
    const opp = nonEmpty(game.opponentName) ? game.opponentName.trim() : '';
    let title = opp ? `${game.homeGame ? 'vs.' : '@'} ${opp}` : (nonEmpty(game.title) ? game.title.trim() : 'Game');
    if (nonEmpty(teamName) && opp) title = `${teamName.trim()} ${title}`;
    if (game.status === 'postponed') title = `POSTPONED: ${title}`;
    let location = nonEmpty(game.venue) ? game.venue.trim() : '';
    if (!location && game.homeGame && teamInfo) {
        location = [teamInfo.stadiumName, teamInfo.address].filter(nonEmpty).map((s: string) => { return s.trim(); }).join(', ');
    }
    const allDay = game.status === 'tbat';
    const day = allDay ? getTbatDate(start) : null;
    return {
        title,
        start: day || start,
        end: day ? new Date(day.getTime() + 24 * HOUR) : new Date(start.getTime() + GAME_HOURS * HOUR),
        allDay,
        location,
        url: `${origin}/games/${encodeURIComponent(game.id)}/info`,
    };
};

export const getEventCalendarItem = (event: any, origin: string): CalendarItem | null => {
    if (!event || event.published === false || event.deleted === true) return null;
    const start = toDate(event.timeOfEvent);
    if (!start) return null;
    const endRaw = toDate(event.endTimeOfEvent);
    return {
        title: nonEmpty(event.title) ? event.title.trim() : 'Event',
        start,
        end: endRaw && endRaw > start ? endRaw : new Date(start.getTime() + EVENT_DEFAULT_HOURS * HOUR),
        allDay: false,
        location: nonEmpty(event.venue) ? event.venue.trim() : '',
        url: `${origin}/events/${encodeURIComponent(event.id)}`,
    };
};

// Still addable until it ends, so the button stays during a live game.
export const isItemAddable = (item: CalendarItem | null, now: Date = new Date()): boolean => {
    return !!item && item.end > now;
};

const pad = (n: number) => { return String(n).padStart(2, '0'); };
const ymd = (d: Date) => { return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}`; };
const utcStamp = (d: Date) => { return `${ymd(d)}T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`; };

export const getGoogleTemplateUrl = (item: CalendarItem): string => {
    const dates = item.allDay ? `${ymd(item.start)}/${ymd(item.end)}` : `${utcStamp(item.start)}/${utcStamp(item.end)}`;
    const qs = new URLSearchParams({ action: 'TEMPLATE', text: item.title, dates, details: item.url });
    if (item.location) qs.set('location', item.location);
    return `https://calendar.google.com/calendar/render?${qs.toString()}`;
};

export const getOutlookComposeUrl = (item: CalendarItem): string => {
    const iso = (d: Date) => { return d.toISOString().replace(/\.\d{3}Z$/, 'Z'); };
    const qs = new URLSearchParams({
        path: '/calendar/action/compose',
        rru: 'addevent',
        subject: item.title,
        startdt: item.allDay ? item.start.toISOString().slice(0, 10) : iso(item.start),
        enddt: item.allDay ? item.end.toISOString().slice(0, 10) : iso(item.end),
        allday: item.allDay ? 'true' : 'false',
        body: item.url,
    });
    if (item.location) qs.set('location', item.location);
    return `https://outlook.live.com/calendar/0/deeplink/compose?${qs.toString()}`;
};

export const getItemIcsPath = (kind: 'game' | 'event', id: string): string => {
    return `/calendar/${kind}/${encodeURIComponent(id)}.ics`;
};

// Option B card colors: the card is the team's primary color, body text is whatever reads on it,
// and the heading/links use the secondary color only when it contrasts with the primary.
export const getCalendarCardColors = (colors: any): { background: string, text: string, accent: string } => {
    const background = colors && nonEmpty(colors.primaryColor) ? colors.primaryColor : '#171d28';
    const text = getReadableTextColor(background, background);
    const secondary = colors && nonEmpty(colors.secondaryColor) ? colors.secondaryColor : '';
    const ratio = secondary ? contrastRatio(secondary, background) : null;
    return { background, text, accent: ratio !== null && ratio >= 3 ? secondary : text };
};

// "Fri, Oct 23 · 6:15 PM · Venue" for the single-item banner (all-day items: "Time TBA").
export const getItemSubheading = (item: CalendarItem): string => {
    const date = item.start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', ...(item.allDay ? { timeZone: 'UTC' } : {}) });
    const time = item.allDay ? 'Time TBA' : item.start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return [date, time, item.location].filter(nonEmpty).join(' · ');
};

export type CalendarMode = 'feed' | 'item';
export type CalendarMethod = 'google' | 'apple' | 'outlook' | 'copy' | 'download';
export interface CalendarItemRef { kind: 'game' | 'event'; id: string; item: CalendarItem | null | any }

// GA4 parameters for one calendar action (the banner's five controls). Values stay under
// GA4's 100-character parameter limit.
export const getCalendarAnalyticsParams = (mode: CalendarMode, method: CalendarMethod, ref?: CalendarItemRef) => {
    const title = ref && ref.item && nonEmpty(ref.item.title) ? ref.item.title.trim().slice(0, 100) : '';
    return {
        calendarMode: mode,
        method,
        kind: mode === 'item' && ref ? ref.kind : 'feed',
        itemId: mode === 'item' && ref && nonEmpty(ref.id) ? ref.id : '',
        itemTitle: mode === 'item' ? title : '',
    };
};
